import type {
  AnyFieldApi,
  AnyFormApi,
  FormValidateOrFn,
} from "@tanstack/react-form";
import { useForm } from "@tanstack/react-form";
import { cn } from "cn";
import * as React from "react";
import { clearFieldError, setFieldError } from "./server-errors";
import { submitForm } from "./submit-form";
import { applyValidationPolicy } from "./validation-policy";

const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]), textarea, select, button, [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

type InlineEditFormHelpers = {
  done: () => void;
};

export type InlineEditSubmitHelpers = {
  /**
   * Attach a server error to the edited field and keep the editor open with
   * the user's input intact. Return after calling it — do not also throw.
   */
  setFieldError: (message: string) => void;
};

type SingleFieldRecord = Record<string, any> & { [K: string]: any };

/** Opens the editor from outside its own view-mode trigger. */
export type InlineEditFormHandle<T extends SingleFieldRecord> = {
  /**
   * Enter edit mode seeded with `values` — unsaved until the user commits,
   * exactly as if they had typed them (blur saves, Escape discards). An editor
   * already open is reseeded. `disabled` gates only the view-mode trigger: the
   * caller decides when to call this (a result arriving in the same tick as the
   * render that re-enables the field must still land).
   */
  edit: (values: T) => void;
  /** The value as it stands — the open editor's unsaved text, else the saved baseline. */
  read: () => T;
};

type InlineEditFormProps<T extends SingleFieldRecord> = {
  defaultValues: T;
  schema: FormValidateOrFn<T>;
  onSubmit: (values: T, helpers: InlineEditSubmitHelpers) => Promise<void>;
  displayValue?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Accessible name for the view-mode edit trigger, e.g. "Edit phone".
   * Without it the accessible name is the whole rendered display value —
   * pass it whenever `displayValue` is markdown or multi-element.
   */
  editLabel?: string;
  children: (
    field: AnyFieldApi,
    helpers: InlineEditFormHelpers,
  ) => React.ReactNode;
  className?: string;
  /** Told whenever the editor opens or closes — for a caller that must not act while it is open. */
  onOpenChange?: (open: boolean) => void;
  ref?: React.Ref<InlineEditFormHandle<T>>;
};

export function InlineEditForm<T extends Record<string, any>>({
  defaultValues,
  schema,
  onSubmit,
  displayValue,
  placeholder = "Not provided",
  disabled,
  editLabel,
  children,
  className,
  onOpenChange,
  ref,
}: InlineEditFormProps<T>) {
  const [open, setOpen] = React.useState(false);
  // Bumped by `edit()` so an editor that is already open remounts its field:
  // rich-text editors read their value only on mount.
  const [seed, setSeed] = React.useState(0);

  const onOpenChangeRef = React.useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  React.useEffect(() => {
    onOpenChangeRef.current?.(open);
  }, [open]);

  const fieldName = React.useMemo(
    () => Object.keys(defaultValues)[0] as string,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const form = useForm({
    defaultValues,
    // `applyValidationPolicy` is deliberately typed loosely (Record<string,
    // unknown>) so one policy helper can serve every form; react-form's
    // `validators` generic carries 9 type params inferred from the object
    // itself, which can't be named from here. Same escape hatch as
    // use-app-form.ts. (react-form 1.33 tightened FormValidators, which is why
    // the previous narrow `as { onChange: FormValidateOrFn<T> }` stopped
    // matching.)
    validators: applyValidationPolicy({ onChange: schema }) as any,
    onSubmit: async ({ value }) => {
      if (isUnchanged()) {
        setOpen(false);
        return;
      }
      clearFieldError(anyForm, fieldName);
      let serverErrorShown = false;
      const helpers: InlineEditSubmitHelpers = {
        setFieldError: (message) => {
          serverErrorShown = true;
          setFieldError(anyForm, fieldName, message);
        },
      };
      try {
        await onSubmit(value as T, helpers);
      } catch {
        // Throwing is the documented "keep the editor open" signal; the consumer
        // has already surfaced the failure (toast / setFieldError). Swallowing
        // here is what stops form-core's rethrow from becoming an unhandled
        // rejection on every failed inline edit.
        return;
      }
      if (serverErrorShown) return; // stay open so the user can fix it
      form.reset(value as T);
      setOpen(false);
    },
  });

  // `fieldName` is a runtime string; the typed FormApi wants DeepKeys<T>.
  // The cast is safe: `fieldName` is literally the single key of `defaultValues`.
  const anyForm = form as unknown as AnyFormApi;

  React.useImperativeHandle(
    ref,
    () => ({
      edit: (values) => {
        anyForm.setFieldValue(fieldName, values[fieldName]);
        setSeed((current) => current + 1);
        setOpen(true);
      },
      read: () => form.state.values as T,
    }),
    [anyForm, form, fieldName],
  );

  // Reset form when external data changes
  const serializedDefaults = JSON.stringify(defaultValues);
  React.useEffect(() => {
    // A background refetch while the editor is open must not clobber the edit
    // in progress.
    if (open) return;
    form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedDefaults, open]);

  const viewRef = React.useRef<HTMLDivElement>(null);
  const editRef = React.useRef<HTMLDivElement>(null);
  // Set by whichever exit path initiated the close. Only keyboard-driven exits
  // return focus to the trigger; a blur exit must not steal focus back from
  // wherever the user just clicked.
  const restoreFocusRef = React.useRef(false);

  React.useEffect(() => {
    if (open) return;
    if (!restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    viewRef.current?.focus();
  }, [open]);

  // Entering edit mode: most children carry `autoFocus`, and pickers opened with
  // `defaultOpen` focus their portal. Focus the first control only when nothing
  // else claimed focus by the next frame.
  React.useEffect(() => {
    if (!open) return;
    const container = editRef.current;
    if (!container) return;
    const raf = requestAnimationFrame(() => {
      const active = document.activeElement;
      if (container.contains(active)) return;
      // A marked node only counts when it is a portal opened FROM the editor —
      // a marked ANCESTOR (a dialog hosting this inline edit) must not.
      const marked = active?.closest("[data-inline-edit-keep-open]");
      if (marked && !marked.contains(container)) return;
      container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // isDefaultValue is form-core's deep-equal comparison against the reset
  // baseline (form.state.isDirty is a STICKY touched flag in 1.29 and stays
  // true after a hand-revert) — but it reports `undefined` as always-unchanged,
  // so clearing a picker to `undefined` would silently discard the edit.
  // Cleared inline-edit values should be `null`, never `undefined`; this guard
  // keeps the save attempt honest either way.
  const isUnchanged = React.useCallback(() => {
    const current = (form.state.values as Record<string, unknown>)[fieldName];
    if (current !== undefined) return form.state.isDefaultValue;
    const baseline = (form.options.defaultValues as Record<string, unknown>)?.[
      fieldName
    ];
    return baseline === undefined;
  }, [form, fieldName]);

  const submitOrClose = React.useCallback(
    (options?: { restoreFocus?: boolean }) => {
      if (form.state.isSubmitting) return;
      restoreFocusRef.current = options?.restoreFocus ?? false;
      if (isUnchanged()) {
        setOpen(false);
        return;
      }
      // Changed → always attempt a submit. `submitForm` validates first, so an
      // invalid value KEEPS THE EDITOR OPEN with its message visible instead of
      // silently reverting the user's typing. Escape is the deliberate discard.
      void submitForm(anyForm);
    },
    [form, anyForm, isUnchanged],
  );

  const cancel = React.useCallback(() => {
    restoreFocusRef.current = true;
    form.reset();
    setOpen(false);
  }, [form]);

  // Leaving the editor saves — and closing it by going away is leaving it.
  // An open editor that unmounts with an edit (the user moved to another
  // page or visit) commits it instead of dropping the typing.
  const openRef = React.useRef(open);
  openRef.current = open;
  React.useEffect(
    () => () => {
      if (!openRef.current || form.state.isSubmitting || isUnchanged()) return;
      void submitForm(anyForm);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Reloading or closing the tab cannot save; the browser asks first.
  React.useEffect(() => {
    if (!open) return;
    const warn = (event: BeforeUnloadEvent) => {
      if (isUnchanged()) return;
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [open, isUnchanged]);

  // Whether the last pointer press landed outside the editor and outside any
  // portal it opened. Read by the blur check: a menu or dialog opened from
  // ELSEWHERE on the page (an Attach menu) is marked keep-open too, and must
  // not hold this editor open — it would never save.
  const pressedOutsideRef = React.useRef(false);
  React.useEffect(() => {
    if (!open) return;
    pressedOutsideRef.current = false;
    const onPointerDown = (event: PointerEvent) => {
      const editor = editRef.current;
      if (!editor || !(event.target instanceof Element)) return;
      const marked = event.target.closest("[data-inline-edit-keep-open]");
      pressedOutsideRef.current =
        !editor.contains(event.target) && !(marked && !marked.contains(editor));
    };
    // A keyboard-opened portal comes from the focused editor itself.
    const onKeyDown = () => {
      pressedOutsideRef.current = false;
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  const helpers = React.useMemo<InlineEditFormHelpers>(
    () => ({
      done: () =>
        submitOrClose({
          // A picker closed by keyboard returns focus to its trigger, which is
          // still inside the editor; a plain input blur has already moved focus
          // elsewhere.
          restoreFocus:
            editRef.current?.contains(document.activeElement) ?? false,
        }),
    }),
    [submitOrClose],
  );

  if (open) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          restoreFocusRef.current = true; // Enter-to-save is keyboard-driven
          void submitForm(anyForm);
        }}
        onKeyDown={(e) => {
          // Only an Escape pressed in the editor itself discards. React
          // bubbles keys from portals opened inside it (the link dialog) up to
          // here; their Escape closes them, never the whole edit.
          if (e.key !== "Escape" || !e.currentTarget.contains(e.target as Node))
            return;
          e.preventDefault();
          cancel();
        }}
        className={className}
      >
        <div
          ref={editRef}
          onBlur={(e) => {
            // Defer so focus has time to land in portal-rendered widgets.
            // Anything marked [data-inline-edit-keep-open] keeps the editor
            // open. Marked today: Dialog, Popover, DropdownMenu, TipTap
            // BubbleMenu — the portals we actually open inside inline edits.
            // If a new combination closes the editor too early, add the marker
            // to that widget's Content.
            const currentTarget = e.currentTarget;
            requestAnimationFrame(() => {
              if (currentTarget.contains(document.activeElement)) return;
              // Keep-open applies only to portals opened FROM the editor. A
              // marked ANCESTOR (a dialog hosting this inline edit) contains
              // the editor itself — focus moving elsewhere inside it must
              // still commit — and a portal opened by a press outside the
              // editor is not the editor's.
              const marked = document.activeElement?.closest(
                "[data-inline-edit-keep-open]",
              );
              if (
                marked &&
                !marked.contains(currentTarget) &&
                !pressedOutsideRef.current
              )
                return;
              submitOrClose({ restoreFocus: false });
            });
          }}
        >
          <form.Field key={seed} name={fieldName}>
            {(field) => children(field, helpers)}
          </form.Field>
        </div>
      </form>
    );
  }

  // View mode
  return (
    <div
      ref={viewRef}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-label={editLabel}
      onClick={() => {
        if (!disabled) setOpen(true);
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key !== "Enter" && e.key !== " ") return;
        // Anchors and nested buttons inside displayValue keep their own
        // activation (markdown links, the line-item "open URL" affordance).
        if (
          (e.target as HTMLElement).closest("a, button, [role='button']") !==
          e.currentTarget
        )
          return;
        e.preventDefault(); // Space would scroll the page
        restoreFocusRef.current = true;
        setOpen(true);
      }}
      className={cn(
        /* sorted */
        "flex items-center",
        "min-h-10",
        "px-3",
        "text-text-dark-max",
        "cursor-pointer",
        "rounded-lg",
        "transition-all",
        "hover:bg-bg-dark-min",
        "focus-visible:outline-2 focus-visible:outline-outline-dark-max focus-visible:outline-offset-1",
        className,
      )}
    >
      {displayValue || (
        <span className="text-text-dark-high text-sm">{placeholder}</span>
      )}
    </div>
  );
}
